import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@traqly/ui/lib/utils";

function Show({
  when,
  children,
}: {
  when: boolean;
  children: React.ReactNode;
}) {
  return when ? <>{children}</> : null;
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

const buttonVariants = cva(
  "relative inline-flex min-h-10 min-w-10 items-center justify-center sm:min-h-0 sm:min-w-0 md:min-h-8 md:min-w-8 lg:min-h-0 lg:min-w-0 [&>svg]:pointer-events-none",
  {
    variants: {
      variant: {
        primary: [
          "not-disabled:bg-yellow-300 not-disabled:border not-disabled:border-mono-300 not-disabled:text-mono-900 not-disabled:cursor-pointer",
          "not-disabled:hover:bg-yellow-500 not-disabled:hover:border-mono-500 not-disabled:hover:text-yellow-700 not-disabled:hover:text-mono-900",
          "not-disabled:active:bg-yellow-900 not-disabled:active:border-yellow-700 not-disabled:active:text-yellow-100",
          "disabled:bg-mono-200 disabled:text-mono-500 disabled:cursor-not-allowed",
        ],
        "primary-error": [
          "not-disabled:bg-error-700 not-disabled:text-white not-disabled:cursor-pointer",
          "not-disabled:hover:bg-error-800 not-disabled:hover:text-white",
          "not-disabled:active:bg-error-500 not-disabled:active:text-white",
          "disabled:bg-mono-200 disabled:text-mono-400 disabled:cursor-not-allowed",
        ],
        secondary: [
          "not-disabled:bg-white not-disabled:border not-disabled:border-mono-200 not-disabled:text-mono-900 not-disabled:cursor-pointer",
          "not-disabled:hover:bg-yellow-100 not-disabled:hover:border-mono-500 not-disabled:hover:text-mono-900",
          "not-disabled:active:bg-yellow-300 not-disabled:active:border-yellow-700 not-disabled:active:text-yellow-900",
          "disabled:bg-white disabled:border-mono-200 disabled:text-mono-500 disabled:cursor-not-allowed",
        ],
        "secondary-error": [
          "not-disabled:bg-white not-disabled:border not-disabled:border-error-700 not-disabled:text-error-700 not-disabled:cursor-pointer",
          "not-disabled:hover:bg-error-050 not-disabled:hover:border not-disabled:hover:border-error-700 not-disabled:hover:text-error-700",
          "not-disabled:active:hover:bg-error-100 not-disabled:active:hover:border not-disabled:active:hover:border-error-700 not-disabled:active:hover:text-error-700",
          "disabled:bg-white disabled:border-mono-200 disabled:text-mono-400 disabled:cursor-not-allowed",
        ],
        tertiary: [
          "not-disabled:bg-transparent not-disabled:text-blue-700! not-disabled:border-none not-disabled:cursor-pointer",
          "not-disabled:hover:bg-transparent not-disabled:hover:text-blue-900!",
          "not-disabled:active:bg-blue-050! not-disabled:active:text-blue-900!",
          "disabled:bg-transparent disabled:text-mono-400! disabled:cursor-not-allowed",
        ],
        "tertiary-error": [
          "not-disabled:bg-transparent not-disabled:text-error-700 not-disabled:cursor-pointer",
          "not-disabled:hover:bg-error-050 not-disabled:hover:text-error-700",
          "not-disabled:active:hover:bg-error-100 not-disabled:active:hover:text-error-700",
          "disabled:bg-transparent disabled:text-mono-400 disabled:cursor-not-allowed",
        ],
        ghost: [
          "not-disabled:bg-white not-disabled:text-mono-700 not-disabled:border-none not-disabled:cursor-pointer",
          "not-disabled:hover:bg-yellow-100 not-disabled:hover:text-mono-900",
          "not-disabled:active:bg-yellow-300 not-disabled:active:text-yellow-900",
          "disabled:bg-white disabled:text-mono-500 disabled:cursor-not-allowed",
        ],
        "ghost-error": [
          "not-disabled:bg-white not-disabled:text-error-700 not-disabled:cursor-pointer",
          "not-disabled:hover:bg-error-050 not-disabled:hover:text-error-700",
          "not-disabled:active:hover:bg-error-100 not-disabled:active:hover:text-error-700",
          "disabled:bg-white disabled:text-mono-400 disabled:cursor-not-allowed",
        ],
        "ghost-transparent": [
          "not-disabled:bg-transparent not-disabled:text-mono-700 not-disabled:border-none not-disabled:cursor-pointer",
          "not-disabled:hover:bg-transparent not-disabled:hover:text-mono-900",
          "not-disabled:active:bg-transparent not-disabled:active:text-mono-900",
          "disabled:bg-transparent disabled:text-mono-500 disabled:cursor-not-allowed",
        ],
        error: [
          "not-disabled:text-error-900 not-disabled:bg-neutral-white not-disabled:border not-disabled:border-mono-200 not-disabled:cursor-pointer",
          "not-disabled:hover:bg-neutral-percent-8 not-disabled:hover:border-neutral-100",
          "not-disabled:active:bg-primary-050 not-disabled:active:border-primary-700",
          "disabled:border-neutral-050 disabled:text-neutral-300 disabled:cursor-not-allowed",
        ],
        destructive: [
          "not-disabled:text-white not-disabled:bg-error-700 cursor-pointer",
          "not-disabled:hover:bg-error-800",
          "not-disabled:active:bg-error-900",
          "disabled:bg-error-200 disabled:text-error-500 disabled:cursor-not-allowed",
        ],
        addOption: [
          "not-disabled:bg-transparent not-disabled:text-blue-700 not-disabled:border-none not-disabled:cursor-pointer",
          "not-disabled:hover:bg-blue-50 not-disabled:hover:text-blue-800",
          "not-disabled:active:bg-blue-100 not-disabled:active:text-blue-900",
          "disabled:bg-transparent disabled:text-mono-400 disabled:cursor-not-allowed",
        ],
      },
      size: {
        sm: "h-6 px-2 gap-1 rounded-md text-button-xsmall",
        md: "h-8 px-3 gap-1.5 rounded-lg text-button-small",
        lg: "h-10 px-4 gap-2 rounded-xl text-button-medium",
        xl: "h-14 px-5 gap-3 rounded-2xl text-button-large border-2",
      },
      content: {
        "icon-only": "",
        "text-only": "",
        "icon-text": "",
        "text-icon": "",
        "icon-text-icon": "",
      },
    },
    compoundVariants: [
      // Icon-only padding adjustments
      { content: "icon-only", size: "sm", class: "px-1" },
      { content: "icon-only", size: "md", class: "px-1.5" },
      { content: "icon-only", size: "lg", class: "px-2" },
      { content: "icon-only", size: "xl", class: "px-3" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      content: "text-only",
    },
  },
);

interface ButtonProps
  extends
    Omit<React.ComponentProps<"button">, "content">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

function Button({
  className,
  variant,
  size,
  content,
  asChild = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  ...props
}: ButtonProps) {
  // Determine content type automatically if not specified
  const hasLeftIcon = Boolean(leftIcon);
  const hasRightIcon = Boolean(rightIcon);
  const hasText = Boolean(children);

  let autoContent:
    | "icon-only"
    | "text-only"
    | "icon-text"
    | "text-icon"
    | "icon-text-icon" = content || "text-only";
  if (!content) {
    if (hasLeftIcon && hasRightIcon && hasText) {
      autoContent = "icon-text-icon";
    } else if (hasLeftIcon && hasText) {
      autoContent = "icon-text";
    } else if (hasRightIcon && hasText) {
      autoContent = "text-icon";
    } else if ((hasLeftIcon || hasRightIcon) && !hasText) {
      autoContent = "icon-only";
    } else {
      autoContent = "text-only";
    }
  }

  if (asChild) {
    const slottableChild =
      isLoading &&
      React.isValidElement<{ children?: React.ReactNode }>(children)
        ? React.cloneElement(children, {
            children: (
              <span className="invisible">{children.props.children}</span>
            ),
          })
        : children;

    return (
      <Slot
        data-slot="button"
        suppressHydrationWarning
        className={cn(
          buttonVariants({ variant, size, content: autoContent, className }),
        )}
        {...props}
      >
        <Show when={isLoading}>
          <SpinnerIcon className="absolute top-1/2 left-1/2 size-6 -translate-1/2 animate-spin" />
        </Show>
        <Show when={!!leftIcon}>
          <span
            className={cn("inline-flex shrink-0", { invisible: isLoading })}
          >
            {leftIcon}
          </span>
        </Show>
        <Slottable>{slottableChild}</Slottable>
        <Show when={!!rightIcon}>
          <span
            className={cn("inline-flex shrink-0", { invisible: isLoading })}
          >
            {rightIcon}
          </span>
        </Show>
      </Slot>
    );
  }

  return (
    <button
      data-slot="button"
      suppressHydrationWarning
      className={cn(
        buttonVariants({ variant, size, content: autoContent, className }),
      )}
      {...props}
    >
      <Show when={isLoading}>
        <SpinnerIcon className="absolute top-1/2 left-1/2 size-6 -translate-1/2 animate-spin" />
      </Show>
      <div className={cn("contents", { invisible: isLoading })}>
        <Show when={!!leftIcon}>
          <span className="inline-flex shrink-0">{leftIcon}</span>
        </Show>
        {children}
        <Show when={!!rightIcon}>
          <span className="inline-flex shrink-0">{rightIcon}</span>
        </Show>
      </div>
    </button>
  );
}

export { Button, buttonVariants, type ButtonProps };
